import React, { useEffect, useMemo, useState } from 'react';
import './TablePagination.css';

/** Default client-side page size for platform list tables. */
export const TABLE_PAGE_SIZE = 50;

export function usePaginatedRows(rows, pageSize = TABLE_PAGE_SIZE) {
  const [page, setPage] = useState(1);
  const total = Array.isArray(rows) ? rows.length : 0;
  const safePageSize = Math.max(1, pageSize);
  const pageCount = Math.max(1, Math.ceil(total / safePageSize));

  useEffect(() => {
    setPage((current) => Math.min(current, pageCount));
  }, [pageCount]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * safePageSize;
    return (Array.isArray(rows) ? rows : []).slice(start, start + safePageSize);
  }, [page, safePageSize, rows]);

  return { page, setPage, pageCount, pageRows, pageSize: safePageSize, total };
}

export default function TablePagination({
  page,
  setPage,
  pageCount,
  pageSize,
  total,
  itemLabel = '',
  pageSizeOptions = null,
  onPageSizeChange = null,
}) {
  if (total <= 0) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const noun = itemLabel ? ` ${itemLabel}` : '';

  return (
    <nav className="table-pagination" aria-label="Table pagination">
      <p className="table-pagination-range">
        {start}–{end} of {total}{noun}
      </p>
      {Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0 && typeof onPageSizeChange === 'function' ? (
        <label className="table-pagination-pagesize">
          <span className="edi-sr-only">Rows per page</span>
          <select
            className="table-pagination-pagesize-select"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value) || pageSize)}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>{size} / page</option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="table-pagination-controls">
        <button
          type="button"
          className="table-pagination-btn"
          aria-label="Previous"
          disabled={page <= 1}
          onClick={() => setPage((value) => value - 1)}
        >
          <svg className="table-pagination-arrow" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M13 8H3M7 4 3 8l4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <span className="table-pagination-page">
          Page {page} of {pageCount}
        </span>
        <button
          type="button"
          className="table-pagination-btn"
          aria-label="Next"
          disabled={page >= pageCount}
          onClick={() => setPage((value) => value + 1)}
        >
          <svg className="table-pagination-arrow" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M3 8h10M9 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </nav>
  );
}
